// ChauffeurFiscalCard — déclaration du profil fiscal du chauffeur (espace pro).
// Permet de sortir des FISCAL_REQUIRES_REVIEW : le Fiscal Engine utilise ce profil
// pour décider FNE/RNE sur chaque course payée. Sandbox uniquement.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const DRIVER_BIZ_ID = "drv_jean_k";
const REGIMES = [
  { id: "MICRO", label: "Micro-entreprise (TEE)" },
  { id: "RSI", label: "Régime simplifié (RSI)" },
  { id: "RNI", label: "Régime réel (RNI)" },
  { id: "UNKNOWN", label: "Je ne sais pas encore" },
];

const inp = {
  width: "100%", padding: "9px 11px", borderRadius: 9, fontSize: 12.5,
  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)",
  color: "#fff", outline: "none", boxSizing: "border-box",
};
const lab = { fontSize: 9.5, fontWeight: 800, color: "#9DB4A6", textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 4, marginTop: 9 };

export const ChauffeurFiscalCard = () => {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ legalName: "", ncc: "", taxRegime: "MICRO", vatStatus: "NON_ASSUJETTI" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/v3/fiscal/profiles/${DRIVER_BIZ_ID}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (b?.profile) {
          setProfile(b.profile);
          setForm({
            legalName: b.profile.legalName || "",
            ncc: b.profile.ncc || "",
            taxRegime: b.profile.taxRegime || "MICRO",
            vatStatus: b.profile.vatStatus || "NON_ASSUJETTI",
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    if (!form.legalName.trim()) { toast.error("Le nom légal est requis"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/v3/fiscal/profiles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ayaBusinessId: DRIVER_BIZ_ID,
          legalName: form.legalName.trim(),
          ncc: form.ncc.trim() || null,
          taxRegime: form.taxRegime,
          vatStatus: form.vatStatus,
          activities: ["TAXI"],
          fneStatus: "SANDBOX", rneStatus: "SANDBOX",
          fiscalProfileStatus: "ACTIVE_SANDBOX",
        }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) { toast.error("Enregistrement impossible"); return; }
      setProfile(b.profile);
      setOpen(false);
      toast.success("Profil fiscal enregistré ✓ — vos reçus seront émis automatiquement");
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  const declared = !!(profile && profile.legalName);
  return (
    <>
      <button className="chf-prow" data-testid="chf-profil-fiscal" onClick={() => setOpen((o) => !o)}>
        <Landmark size={16} color="#34D399" style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <h4>Profil fiscal</h4>
          <span style={{ fontSize: 9.5, color: "#9DB4A6", fontWeight: 700 }}>
            {declared ? `${profile.legalName} · ${profile.taxRegime}` : "À déclarer pour des reçus automatiques"}
          </span>
        </span>
        {declared && <span style={{ padding: "3px 9px", borderRadius: 999, background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.5)", color: "#34D399", fontSize: 9, fontWeight: 800 }}>Déclaré ✓</span>}
        <ChevronRight size={15} color="#9DB4A6" style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
      </button>
      {open && !loaded && (
        <div className="chf-card" style={{ marginTop: -4, textAlign: "center", fontSize: 11, color: "#9DB4A6" }}>⏳ Chargement du profil…</div>
      )}
      {open && loaded && (
        <div className="chf-card" data-testid="chf-fiscal-form" style={{ marginTop: -4 }}>
          <span style={lab}>Nom légal / raison sociale *</span>
          <input data-testid="chf-fiscal-legalname" style={inp} value={form.legalName}
            onChange={(e) => setForm((f) => ({ ...f, legalName: e.target.value }))} placeholder="Ex : Jean Konaté — Taxi compteur" />
          <span style={lab}>NCC (numéro contribuable, optionnel)</span>
          <input data-testid="chf-fiscal-ncc" style={inp} value={form.ncc}
            onChange={(e) => setForm((f) => ({ ...f, ncc: e.target.value }))} placeholder="Ex : CI-2026-1234567A" />
          <span style={lab}>Régime fiscal</span>
          <select data-testid="chf-fiscal-regime" style={inp} value={form.taxRegime}
            onChange={(e) => setForm((f) => ({ ...f, taxRegime: e.target.value }))}>
            {REGIMES.map((r) => <option key={r.id} value={r.id} style={{ color: "#111" }}>{r.label}</option>)}
          </select>
          <span style={lab}>TVA</span>
          <select data-testid="chf-fiscal-tva" style={inp} value={form.vatStatus}
            onChange={(e) => setForm((f) => ({ ...f, vatStatus: e.target.value }))}>
            <option value="NON_ASSUJETTI" style={{ color: "#111" }}>Non assujetti à la TVA</option>
            <option value="ASSUJETTI_TVA" style={{ color: "#111" }}>Assujetti à la TVA (18 %)</option>
          </select>
          <button data-testid="chf-fiscal-save" onClick={save} disabled={saving} style={{
            marginTop: 12, width: "100%", padding: "11px 12px", borderRadius: 10,
            background: "linear-gradient(135deg, #10B981, #0E7A4F)", color: "#fff",
            border: "none", fontWeight: 800, fontSize: 13, cursor: "pointer", opacity: saving ? 0.6 : 1,
          }}>{saving ? "Enregistrement…" : "Enregistrer mon profil fiscal"}</button>
          <div style={{ fontSize: 9, color: "#9DB4A6", marginTop: 7, textAlign: "center" }}>
            Mode SANDBOX — aucune transmission réelle à la DGI. Vos courses payées génèrent des reçus (RNE) automatiques.
          </div>
        </div>
      )}
    </>
  );
};
